import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { FormattedMessage } from 'react-intl';
import { ReactNode } from 'react';
import { UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { hasRole, padZeroLeft } from '@synergy/utils';
import urljoin from 'url-join';

import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../pool-autocomplete-list';
import { BonusSettlementSearchCustomer } from '../../../../features/bonus-settlements/detail/models/bonus-settlement-supplier-overview';
import { CustomerApi } from '../../../api/customer-api';
import { PoolAutocompleteListAction } from '../pool-autocomplete-list-action';
import { PoolAutocompleteListFooter } from '../pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../abstract/form-autocomplete-list';
import { ROUTES } from '../../../../features/sidebar-menu/navigation-drawer/routes';
import { User } from '../../../models/user';

interface PartialCustomer extends BaseAutocompleteSuggestionType {
    name: string;
    city: string;
    isMain?: ReactNode;
}

interface PoolCustomerBonusSettlementAutocompleteListBaseProps {
    user: User;
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
}

class PoolCustomerBonusSettlementAutocompleteListBase extends PoolFormAutocompleteList<
    BonusSettlementSearchCustomer,
    PartialCustomer,
    PoolCustomerBonusSettlementAutocompleteListBaseProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];
    private api = new CustomerApi(API_BASE_URL);
    private apiAbortController?: AbortController;
    private searchParams = new URLSearchParams({ limit: '40' });

    public footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.navigation.customers"
            url={urljoin(BASE_URL, ROUTES.contactsAndOrganizations.customers.new)}
        />
    );

    public label = (<FormattedMessage id="app.label.customer" />);

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public getSuggestionValue = (suggestion: PartialCustomer) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        this.searchParams.set('search', search);
        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.getCustomerBonusSettlements(
            this.props.bonusSettlementId,
            this.props.supplierBonusSettlementId,
            this.searchParams,
            this.apiAbortController.signal
        );
        if (!response.success && response.requestCanceled) {
            return;
        }
        const items = response.success ? response.result.items : [];
        this.setState({ items });
    }

    public getPartial = (items: BonusSettlementSearchCustomer[]): PartialCustomer[] =>
        items.map(customer => {
            const editLink = urljoin(
                BASE_URL,
                ROUTES.contactsAndOrganizations.customers.detail.replace(':id', customer.id.toString())
            );
            return {
                id: padZeroLeft(customer.id, 5),
                name: customer.name,
                city: customer.city || '',
                actions: <PoolAutocompleteListAction url={editLink} allowedRoles={this.actionRoles} />,
            };
        });
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
});

export const PoolCustomerBonusSettlementAutocompleteList = connect(
    mapStateToProps,
    {}
)(PoolCustomerBonusSettlementAutocompleteListBase);
